import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

const RIGHT_KEYS = ["fld_view", "fld_entry", "fld_edit", "fld_delete"];

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function SentinelPermissionModules() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);

  const [memberId, setMemberId] = useState(params.get("member_id") || "");
  const [searchMemberId, setSearchMemberId] = useState(params.get("member_id") || "");

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [modules, setModules] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchInitialData(local, token);
  }, [navigate]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const fetchInitialData = async (local, token) => {
    try {
      setLoading(true);

      const [dashboardRes, modulesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/sentinel_rights/modules`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!modulesRes.ok) throw new Error("Failed to fetch modules");

      const dashboardResult = await dashboardRes.json();
      const modulesResult = await modulesRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const moduleRows = modulesResult?.data || [];
      setModules(moduleRows);

      if (memberId) {
        await fetchPermissions(memberId, token, moduleRows);
      } else {
        const blank = buildBlankPermissions(moduleRows);
        setPermissions(blank);
        setOriginalPermissions(JSON.parse(JSON.stringify(blank)));
      }
    } catch (error) {
      console.error("Sentinel permission page fetch error:", error);
      setErrorMessage(error.message || "Failed to load permission page.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async (id, tokenArg, moduleArg) => {
    try {
      setErrorMessage("");
      setSuccessMessage("");

      const token = tokenArg || localStorage.getItem("token") || localStorage.getItem("authToken");
      const moduleRows = moduleArg || modules;

      const res = await fetch(`${API_BASE_URL}/sentinel_rights/${encodeURIComponent(id)}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch permissions");
      }

      const grouped = mergeModulesWithSavedRights(moduleRows, data.data || []);

      setPermissions(grouped);
      setOriginalPermissions(JSON.parse(JSON.stringify(grouped)));
    } catch (error) {
      console.error("Sentinel permission fetch error:", error);

      const blank = buildBlankPermissions(modules);
      setPermissions(blank);
      setOriginalPermissions(JSON.parse(JSON.stringify(blank)));
      setErrorMessage(error.message || "Failed to fetch permissions.");
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchMemberId.trim()) {
      setErrorMessage("Please enter member ID.");
      return;
    }

    const id = searchMemberId.trim();
    setMemberId(id);

    window.history.pushState({}, "", `?member_id=${encodeURIComponent(id)}`);

    setLoading(true);
    await fetchPermissions(id);
    setLoading(false);
  };

  const updatePermission = (moduleName, index, key, value) => {
    setPermissions((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy[moduleName][index][key] = value;
      return copy;
    });
  };

  const toggleAllInModule = (moduleName, checked) => {
    setPermissions((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));

      copy[moduleName] = copy[moduleName].map((row) => ({
        ...row,
        fld_view: checked,
        fld_entry: checked,
        fld_edit: checked,
        fld_delete: checked,
      }));

      return copy;
    });
  };

  const handleReset = () => {
    setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
    setSuccessMessage("Permissions reset successfully.");
  };

  const handleSave = async () => {
    try {
      if (!memberId.trim()) {
        setErrorMessage("Please enter member ID before saving.");
        return;
      }

      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      const rights = flattenPermissions(permissions);

      const res = await fetch(
        `${API_BASE_URL}/sentinel_rights/${encodeURIComponent(memberId)}/save`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            rights,
            created_by_admin_id: Number(localStorage.getItem("admin_id") || 1),
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save permissions");
      }

      setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
      setSuccessMessage("Sentinel permissions saved successfully!");
      navigate("/sentinel_user_list");
    } catch (error) {
      console.error("Sentinel permission save error:", error);
      setErrorMessage(error.message || "Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    let totalPages = 0;
    let activePermissions = 0;

    Object.values(permissions).forEach((rows) => {
      totalPages += rows.length;

      rows.forEach((row) => {
        RIGHT_KEYS.forEach((key) => {
          if (row[key]) activePermissions++;
        });
      });
    });

    return {
      totalModules: Object.keys(permissions).length,
      totalPages,
      activePermissions,
      totalSlots: totalPages * 4,
    };
  }, [permissions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading sentinel permissions...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel_permission_modules"
      activeMenu="users"
    >
      <PageHeader onBack={() => navigate("/sentinel_user_list")} />

      {successMessage ? <Alert type="success" message={successMessage} /> : null}
      {errorMessage ? <Alert type="error" message={errorMessage} /> : null}

      <FormSection title="Search Sentinel Guard" subtitle="Enter member ID to load permissions">
        <form onSubmit={handleSearch} className="flex gap-3 flex-wrap">
          <input
            value={searchMemberId}
            onChange={(e) => setSearchMemberId(e.target.value)}
            placeholder="Enter Member ID. Example: MEM008"
            className="w-full md:w-80 px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
            style={{ fontFamily: FONT_UI }}
          />

          <button
            type="submit"
            className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20 bg-gradient-to-br from-slate-800 to-blue-600"
            style={{ fontFamily: FONT_UI }}
          >
            Load Permissions
          </button>
        </form>
      </FormSection>

      <StatsCards stats={stats} memberId={memberId} />

      <FormSection
        title="Sentinel Page Rights"
        subtitle="Enable or disable View, Entry, Edit and Delete permissions"
      >
        {Object.keys(permissions).length === 0 ? (
          <EmptyState>No modules found.</EmptyState>
        ) : (
          <div className="grid gap-5">
            {Object.entries(permissions).map(([moduleName, rows]) => (
              <PermissionModuleCard
                key={moduleName}
                moduleName={moduleName}
                rows={rows}
                onChange={updatePermission}
                onToggleAll={toggleAllInModule}
              />
            ))}
          </div>
        )}
      </FormSection>

      <div className="flex justify-end gap-3 flex-wrap mt-2 mb-5">
        <button
          type="button"
          onClick={() => navigate("/sentinel_user_list")}
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
          style={{ fontFamily: FONT_UI }}
        >
          Back to User List
        </button>

        <button
          type="button"
          onClick={handleReset}
          disabled={saving || Object.keys(permissions).length === 0}
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
          style={{ fontFamily: FONT_UI }}
        >
          Reset
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || Object.keys(permissions).length === 0}
          className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed bg-gradient-to-br from-slate-800 to-blue-600"
          style={{ fontFamily: FONT_UI }}
        >
          {saving ? "Saving..." : "Save Permissions"}
        </button>
      </div>
    </Layout>
  );
}

function buildBlankPermissions(modules = []) {
  const grouped = {};

  modules.forEach((mod) => {
    const moduleName = mod.module_name || "Unknown Module";

    grouped[moduleName] = [];

    grouped[moduleName].push({
      page_id: mod.page_id,
      subpage_id: null,
      fld_department: moduleName,
      fld_submodules: "",
      fld_view: false,
      fld_entry: false,
      fld_edit: false,
      fld_delete: false,
    });

    (mod.submodules || []).forEach((sub) => {
      grouped[moduleName].push({
        page_id: mod.page_id,
        subpage_id: sub.subpage_id,
        fld_department: moduleName,
        fld_submodules: sub.submodule_name || "",
        fld_view: false,
        fld_entry: false,
        fld_edit: false,
        fld_delete: false,
      });
    });
  });

  return grouped;
}

function mergeModulesWithSavedRights(modules = [], saved = []) {
  const savedMap = {};

  saved.forEach((row) => {
    const key = `${Number(row.page_id)}_${row.subpage_id ? Number(row.subpage_id) : "main"}`;
    savedMap[key] = row;
  });

  const grouped = {};

  modules.forEach((mod) => {
    const moduleName = mod.module_name || "Unknown Module";

    grouped[moduleName] = [];

    const mainKey = `${Number(mod.page_id)}_main`;
    const mainSaved = savedMap[mainKey];

    grouped[moduleName].push({
      page_id: mod.page_id,
      subpage_id: null,
      fld_department: moduleName,
      fld_submodules: "",
      fld_view: toBoolean(mainSaved?.fld_view),
      fld_entry: toBoolean(mainSaved?.fld_entry),
      fld_edit: toBoolean(mainSaved?.fld_edit),
      fld_delete: toBoolean(mainSaved?.fld_delete),
    });

    (mod.submodules || []).forEach((sub) => {
      const key = `${Number(mod.page_id)}_${Number(sub.subpage_id)}`;
      const subSaved = savedMap[key];

      grouped[moduleName].push({
        page_id: mod.page_id,
        subpage_id: sub.subpage_id,
        fld_department: moduleName,
        fld_submodules: sub.submodule_name || "",
        fld_view: toBoolean(subSaved?.fld_view),
        fld_entry: toBoolean(subSaved?.fld_entry),
        fld_edit: toBoolean(subSaved?.fld_edit),
        fld_delete: toBoolean(subSaved?.fld_delete),
      });
    });
  });

  return grouped;
}

function flattenPermissions(permissions = {}) {
  const rows = [];

  Object.values(permissions).forEach((moduleRows) => {
    moduleRows.forEach((row) => {
      rows.push({
        page_id: Number(row.page_id),
        subpage_id:
          row.subpage_id === null || row.subpage_id === undefined || row.subpage_id === ""
            ? null
            : Number(row.subpage_id),
        fld_department: row.fld_department || "",
        fld_submodules: row.fld_submodules || null,
        fld_view: !!row.fld_view,
        fld_entry: !!row.fld_entry,
        fld_edit: !!row.fld_edit,
        fld_delete: !!row.fld_delete,
      });
    });
  });

  return rows;
}

function toBoolean(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value).toLowerCase() === "true" ||
    String(value).toUpperCase() === "YES"
  );
}

function PageHeader({ onBack }) {
  const skin = useSkin();

  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Sentinel Permission Modules
        </h2>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Manage module access permissions for sentinel guards
        </p>
      </div>

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3
            className="m-0 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            Sentinel User Permission Control
          </h3>
          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Update view, entry, edit and delete access
          </p>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20"
          style={{
            background: skin.gradient,
            fontFamily: FONT_UI,
          }}
        >
          Back to User List
        </button>
      </div>
    </>
  );
}

function StatsCards({ stats, memberId }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
      <StatCard title="Member ID" value={memberId || "-"} />
      <StatCard title="Total Modules" value={stats.totalModules} />
      <StatCard title="Total Pages" value={stats.totalPages} />
      <StatCard title="Active Permissions" value={`${stats.activePermissions} / ${stats.totalSlots}`} />
    </div>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="bg-white rounded-2xl px-5 py-5 shadow-sm border border-slate-100">
      <p className="text-sm text-slate-500 mb-1" style={{ fontFamily: FONT_UI }}>
        {title}
      </p>
      <h3 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: FONT_HEADING }}>
        {value}
      </h3>
    </div>
  );
}

function PermissionModuleCard({ moduleName, rows, onChange, onToggleAll }) {
  const skin = useSkin();

  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
      <div
        className="text-white px-5 py-4 flex justify-between items-center gap-3 flex-wrap"
        style={{ background: skin.gradient }}
      >
        <div className="text-[17px] font-semibold" style={{ fontFamily: FONT_UI }}>
          {moduleName}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onToggleAll(moduleName, true)}
            className="px-3 py-1.5 rounded-lg bg-white/20 text-white text-xs font-semibold"
            style={{ fontFamily: FONT_UI }}
          >
            Enable All
          </button>

          <button
            type="button"
            onClick={() => onToggleAll(moduleName, false)}
            className="px-3 py-1.5 rounded-lg bg-white/20 text-white text-xs font-semibold"
            style={{ fontFamily: FONT_UI }}
          >
            Disable All
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ fontFamily: FONT_UI }}>
          <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Page / Sub Module</th>
              <th className="text-center px-4 py-3 font-semibold">View</th>
              <th className="text-center px-4 py-3 font-semibold">Entry</th>
              <th className="text-center px-4 py-3 font-semibold">Edit</th>
              <th className="text-center px-4 py-3 font-semibold">Delete</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${row.page_id}-${row.subpage_id || "main"}`}
                className="border-b border-slate-100"
              >
                <td className="px-4 py-3 text-slate-900 font-medium">
                  {row.subpage_id ? row.fld_submodules : "Main Module"}
                </td>

                {RIGHT_KEYS.map((key) => (
                  <td key={key} className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={!!row[key]}
                      onChange={(e) => onChange(moduleName, index, key, e.target.checked)}
                      className="h-4 w-4"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormSection({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
      <div className="mb-5">
        <h3
          className="m-0 text-[22px] font-normal text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          {title}
        </h3>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {subtitle}
        </p>
      </div>
      {children}
    </div>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div
      className="p-4 rounded-xl border border-dashed border-slate-300 text-slate-500 text-sm bg-slate-50"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </div>
  );
}